/* eslint no-unused-vars: ["error", { "varsIgnorePattern": "^Control$" }]*/
var kind    = require("enyo/kind"),
    master  = require("enyo/master"),
    Signals = require("enyo/Signals"),
    Control = require("enyo/Control");

// We really only ever need to do this once.
if (!Control.prototype.__isLazyControl) {

    /**
     * The stock enyo.master does not waterfall resize events to hidden
     * components. This is probably done for performance reasons, but it does
     * mean the components might not flow right once they get shown again.
     *
     * Because we override [enyo.Control]{@link enyo.Control} to be able to
     * defer reflows for hidden components, we can now afford to let resize
     * events waterfall to hidden components without incurring a hefty
     * performance penalty and still have them reflow as soon as they are shown
     * again.
     *
     * @member   enyo.master
     */

    /**
     * Override enyo.master such that it waterfalls resize events to all
     * components, regardless if they're hidden.
     *
     * @param    {string}  nom    The event name
     * @param    {Event=}  event  In event
     * @memberof enyo
     * @private
     */
    master.bubble = function(nom, event) {
        // Enyo.log('master event: ' + nom);
        if (nom === "onresize") {
            // Resize is special; waterfall this message. This works because
            // master is a Component, so it waterfalls to its owned Components
            // (i.e., master has no children).
            master.waterfallDown("onresize");
            master.waterfallDown("onpostresize");
        }
        else {
            // All other top-level events are sent only to interested Signal
            // receivers.
            Signals.send(nom, event);
        }
    };

    /**
     * These overrides to enyo.Control implement an alternative strategy for
     * dealing with `showing` and `absoluteShowing` state.
     *
     * The new strategy is as follows:
     *  - Any time a control is rendered, it's parent is changed or it's
     *    `showing` state is changed, we re-evaluated if the control is
     *    `absoluteShowing`.
     *  - If the re-evaluated `absoluteShowing` state is different from the
     *    current state an `onShowingChanged` event will only be waterfalled.
     *  - The [showingChangedHandler]{@link enyo.Control#showingChangedHandler}
     *    evaluates if the incoming `absoluteShowing` state applies to the
     *    control and updates the `absoluteShowing` flag if needed.
     *
     * This should ensure the `absoluteShowing` state of controls is now always
     * accurate and can safely be used to defer expensive actions for hidden
     * controls.
     *
     * @class enyo.Control
     * @protected
     */
    module.exports = Control.extend(/** @lends enyo.Control.prototype */ {

        /**
         * Flag, tells whether the control has been extended by LazyControl.
         *
         * @private
         */
        __isLazyControl: true,

        /**
         * The control is `absoluteShowing` if itself and all it's ancestors are
         * showing. Don't change this value manually, it will be computed
         * automatically.
         *
         *
         * @readonly
         * @type {boolean}
         * @public
         */
        absoluteShowing: false,

        /**
         * Internal flag indicating if a [reflow]{@link enyo.Control#reflow} has
         * been deferred.
         *
         * @type {boolean}
         * @private
         */
        _needsReflow: false,

        /**
         * If we have a new parent, we need to re-evaluate if our
         * absoluteShowing state.
         *
         * @memberof enyo.Control.prototype
         * @private
         */
        parentChanged: kind.inherit(function(sup) {
            return function() {
                sup.apply(this, arguments);
                this.refreshAbsoluteShowing();
            };
        }),

        /**
         * Gets the parent if it is also a [control]{@link enyo.Control}.
         * Otherwise, return null.
         *
         * @return   {enyo.Control}  The parent control, or `null`.
         * @memberof enyo.Control.prototype
         * @public
         */
        getParentControl: function() {
            var parent = this.parent;

            if (!parent || !(parent instanceof Control)) {
                return null;
            }

            return parent;
        },

        /**
         * Synchronizes the DOM display style with the showing state and
         * triggers re-evaluation of the absoluteShowing state.
         *
         * If the `renderOnShow` flag is set to `true`, the control just became
         * `showing` and has not yet been rendered, it will get rendered now.
         *
         * @param    {boolean}  wasShowing  Whether the control was showing.
         * @memberof enyo.Control.prototype
         * @private
         */
        showingChanged: function(wasShowing) {
            var isShowing = this.showing,
                nextControl;

            // If we are changing from not showing to showing we attempt to find
            // whatever our last known value for display was or use the default
            if (!wasShowing && isShowing) {
                this.applyStyle("display", this._display || "");

                // Note the check for generated and canGenerate as changes to
                // canGenerate will force us to ignore the renderOnShow value so
                // we don't undo whatever the developer was intending
                if (!this.generated && !this.canGenerate && this.renderOnShow) {
                    if (!this.addBefore) {
                        nextControl = this.getNextControl();

                        if (nextControl) {
                            this.addBefore = nextControl;
                        }
                    }

                    this.set("canGenerate", true);
                    this.render();

                    // The render will have already refreshed absoluteShowing
                    // state. No need to do it again.
                    return;
                }
            }

            // If we are supposed to be hiding the control then we need to cache
            // our current display state
            else if (wasShowing && !isShowing) {
                // We can't truly cache this because it _could_ potentially be
                // set to multiple values throughout its life cycle although
                // that seems highly unlikely...
                this._display = this.hasNode() ? this.node.style.display : "";
                this.applyStyle("display", "none");
            }

            this.refreshAbsoluteShowing();
        },

        /**
         * Evaluates if this control and all parents are showing. If the result
         * is different from the last known state, an `onShowingChanged` event
         * will be waterfalled.
         *
         * @param    {boolean=}  ignoreCache  If `true`, cached
         *                                    `absoluteShowing` properties are
         *                                    ignored and the lineage of parent
         *                                    Controls will be evaluated.
         * @memberof enyo.Control.prototype
         * @private
         */
        refreshAbsoluteShowing: function(ignoreCache) {
            var wasAbsoluteShowing = this.absoluteShowing,
                isAbsoluteShowing;

            if (ignoreCache) {
                isAbsoluteShowing = this.calcAbsoluteShowing(true);
            }
            else {
                isAbsoluteShowing = this.calcAbsoluteShowingFast();
            }

            // Waterfall the real visibility
            if (isAbsoluteShowing !== wasAbsoluteShowing) {
                this.waterfall("onShowingChanged", {
                    originator: this,
                    showing:    isAbsoluteShowing
                });
            }
        },

        /**
         * Determines if a Control and all of its parent Controls are showing.
         *
         * @param    {boolean=}  ignoreBounds  If `true`, it will not force a
         *                                     layout by retrieving computed
         *                                     bounds and rely on the return
         *                                     from showing exclusively.
         * @return   {boolean}   Whether the control is showing (visible).
         * @memberof enyo.Control.prototype
         * @public
         */
        calcAbsoluteShowing: function(ignoreBounds) {
            var bounds = !ignoreBounds ? this.getBounds() : null,
                node   = this.hasNode(),
                parentControl;

            // If we're not showing, we're not absoluteShowing. Unlike enyo's
            // usual implementation, we need to check for a parentNode, because
            // a control can have a node which is not attached to the DOM while
            // still referencing a parentControl that is connected to the DOM.
            if (!node || !node.parentNode || this.destroyed || !this.showing || (bounds
            && bounds.height === 0 && bounds.width === 0)) {
                return false;
            }

            // Find closest ancestor that is also a Control
            parentControl = this.getParentControl();

            // Determine if parentControl is absoluteShowing.
            if (parentControl) {
                // Verify if parentControls' ancestry is showing
                return parentControl.calcAbsoluteShowing(ignoreBounds);
            }

            return true;
        },

        /**
         * Faster alternative for [calcAbsoluteShowing]{@link
         * calcAbsoluteShowing}. It does *not* take into account computed bounds
         * and only inspects the closest parent Control's cached absoluteShowing
         * state.
         *
         * @return   {boolean}  Whether the control is showing (visible).
         * @memberof enyo.Control.prototype
         * @public
         */
        calcAbsoluteShowingFast: function() {
            var node = this.hasNode(),
                parentControl;

            // If we're not showing, we're not absoluteShowing.
            if (!node || !node.parentNode || this.destroyed || !this.showing) {
                return false;
            }

            // Find closest ancestor that is also a Control
            parentControl = this.getParentControl();

            // Determine if parentControl is absoluteShowing.
            if (parentControl) {
                return parentControl.absoluteShowing;
            }

            return true;
        },

        /**
         * Here to maintain compatibility with native control implementation.
         * Instead of always firing the `onShowingChanged` event, we refresh the
         * `absoluteShowing` state. This will fire the `onShowingChanged` event
         * if needed.
         *
         * @memberof enyo.Control.prototype
         * @private
         */
        sendShowingChangedEvent: function() {
            this.refreshAbsoluteShowing();
        },

        /**
         * Handles the `onShowingChanged` event that is waterfalled by controls
         * when their `showing` value is modified. If the parent control is
         * showing but the control is not, propagation is stopped and
         * `absoluteShowing` state is re-evaluated.
         *
         * @param    {enyo.Control}  sender  The sender
         * @param    {Object}        event   The event
         * @return   {boolean}       Whether to stop propagation of the event.
         * @memberof enyo.Control.prototype
         * @private
         */
        showingChangedHandler: function(sender, event) {

            // The parent control may be showing, but this control is not.
            if (event.showing && (!this.showing || !this.hasNode())) {
                this.refreshAbsoluteShowing();

                return true;
            }

            // It's save to inherit the waterfalled `absoluteShowing` state.
            this.set("absoluteShowing", event.showing);

            return false;
        },

        /**
         * If reflow was deferred before, trigger [maybeReflow]{@link
         * enyo.Control#maybeReflow}, so reflow will be executed if control just
         * became absoluteShowing.
         *
         * @memberof enyo.Control.prototype
         * @private
         */
        absoluteShowingChanged: function() {
            this._needsReflow && this.maybeReflow();
        },

        /**
         * Returns `true` if this control and all ancestors are showing. When
         * ignoring bounds, te cached `absoluteShowing` state is be returned.
         *
         * @param    {boolean=}  ignoreBounds  If `true`, it will not force a
         *                                     layout by retrieving computed
         *                                     bounds and rely on the return
         *                                     from showing exclusively.
         * @return   {boolean}   Whether the control is showing (visible).
         * @memberof enyo.Control.prototype
         * @public
         */
        getAbsoluteShowing: function(ignoreBounds) {
            if (!ignoreBounds) {
                return this.calcAbsoluteShowing(false);
            }

            return this.absoluteShowing;
        },

        /**
         * Overrides the original implementation and defer reflow if this
         * control is not `absoluteShowing`.
         *
         * @memberof enyo.Control.prototype
         * @private
         */
        maybeReflow: function() {
            if (this.absoluteShowing) {
                this.reflow();
                this._needsReflow = false;
            }
            else {
                this._needsReflow = true;
            }
        },

        /**
         * Overrides the original implementation and calls [maybeReflow]{@link
         * enyo.Control#maybeReflow} rather than [reflow]{@link
         * enyo.Control#reflow} so reflow will be deferred if control is not
         * absoluteShowing.
         *
         * @memberof enyo.Control.prototype
         * @private
         */
        fitChanged: function() {
            this.parent && this.parent.maybeReflow();
        },

        /**
         * Overrides the original implementation and calls [maybeReflow]{@link
         * enyo.Control#maybeReflow} rather than [reflow]{@link
         * enyo.Control#reflow} so reflow will be deferred if control is not
         * absoluteShowing.
         *
         * @memberof enyo.Control.prototype
         * @private
         */
        handleResize: function() {
            this.maybeReflow();
        },

        /**
         * All logic that determines whether a reflow should be deferred or
         * executed immediately has been moved outside the scope of this method.
         * This means it is now safe to override this method to implement any
         * tasks we want to execute on reflow.
         *
         * @memberof enyo.Control.prototype
         * @public
         */
        reflow: function() {
            if (this.layout) {
                this.layout.reflow();
            }
        },

        /**
         * Reflow and re-evaluate `absoluteShowing` state when the control has
         * been rendered.
         *
         * @memberof enyo.Control.prototype
         * @private
         */
        rendered: function() {
            var i,
                c;

            // CAVEAT: Currently we use one entry point ('reflow') for
            // post-render layout work *and* post-resize layout work.
            this.maybeReflow();

            // Make sure showing state is broadcasted
            this.refreshAbsoluteShowing();

            for (i = 0, c;
                (c = this.children[i]); i++) {
                if (c.generated) {
                    c.rendered();
                }
            }
        },

        /**
         * Refresh absoluteShowing state when node gets removed from DOM.
         *
         * @method
         * @private
         */
        removeNodeFromDom: kind.inherit(function(sup) {
            return function() {
                sup.apply(this, arguments);
                this.refreshAbsoluteShowing();
            };
        }),

        /**
         * Refresh absoluteShowing state when control is no longer `generated`.
         *
         * @method
         * @private
         */
        generatedChanged: kind.inherit(function(sup) {
            return function() {
                sup.apply(this, arguments);

                !this.generated && this.refreshAbsoluteShowing();
            };
        })

    });
}
